/** OnlyFans vault payloads → what the Vault page shows (shapes verified live 2026-10-07). */

export type MediaType = 'photo' | 'video' | 'gif' | 'audio'

/** Feather icon for each media type (category rows, tiles). */
export const TYPE_ICON: Record<MediaType, string> = { photo: 'image', video: 'play', gif: 'film', audio: 'mic' }

export interface TypeCounts {
  photo: number
  video: number
  gif: number
  audio: number
}

export interface Preview {
  type: MediaType
  url: string
}

export interface Category {
  id: string
  name: string
  counts: TypeCounts
  previews: Preview[]
  canUpdate: boolean
  canDelete: boolean
  /** A Category the creator made (OnlyFans type "custom"); system lists like Messages are not. */
  custom: boolean
}

export interface VaultItem {
  id: number
  type: MediaType
  createdAt: string
  /** Seconds; 0 for photos. */
  duration: number
  thumb: string
  /**
   * OnlyFans' 300×300 thumb when there is one: a tenth of the pixels of the
   * 960×960 squarePreview, and every one goes through the account proxy.
   */
  small?: string
  /** Full-size photo, or the video/audio file. */
  full: string
  /** Categories this item is in (OnlyFans listStates.hasMedia). */
  inCategories: string[]
  /** Categories OnlyFans would accept it into (listStates.canAddMedia). */
  addableTo: string[]
}

/** The pseudo-category OnlyFans answers for the whole Vault. */
export const ALL_MEDIA = 'all'

type Raw = Record<string, unknown>

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

function asType(v: unknown): MediaType {
  return v === 'video' || v === 'gif' || v === 'audio' ? v : 'photo'
}

export function countsOf(raw: Raw | undefined): TypeCounts {
  return {
    photo: num(raw?.photosCount),
    video: num(raw?.videosCount),
    gif: num(raw?.gifsCount),
    audio: num(raw?.audiosCount),
  }
}

export function totalOf(c: TypeCounts): number {
  return c.photo + c.video + c.gif + c.audio
}

function previewsOf(raw: Raw | undefined): Preview[] {
  const list = Array.isArray(raw?.medias) ? (raw.medias as Raw[]) : []
  return list.flatMap((m) => (typeof m.url === 'string' ? [{ type: asType(m.type), url: m.url }] : []))
}

export function toCategory(raw: Raw): Category {
  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    counts: countsOf(raw),
    previews: previewsOf(raw),
    canUpdate: raw.canUpdate === true,
    canDelete: raw.canDelete === true,
    custom: raw.type === 'custom',
  }
}

export function allMediaOf(payload: Raw | undefined): Category {
  const all = (payload?.all ?? {}) as Raw
  return { id: ALL_MEDIA, name: '', counts: countsOf(all), previews: previewsOf(all), canUpdate: false, canDelete: false, custom: false }
}

function fileUrl(raw: Raw, keys: string[]): string {
  const files = (raw.files ?? {}) as Record<string, { url?: unknown } | undefined>
  for (const k of keys) {
    const url = files[k]?.url
    if (typeof url === 'string' && url) return url
  }
  return ''
}

const thumbOf = (raw: Raw) => fileUrl(raw, ['squarePreview', 'thumb', 'preview'])
const smallOf = (raw: Raw) => fileUrl(raw, ['thumb', 'squarePreview', 'preview'])
const fullOf = (raw: Raw) => fileUrl(raw, ['full', 'preview', 'squarePreview', 'thumb'])

/**
 * What the viewer plays for a video: OnlyFans' streaming transcode (720, then
 * 480, 240). The original upload (files.full) can be hundreds of MB at a
 * bitrate the account proxy cannot stream, so anything past a short clip stalls.
 */
function streamOf(raw: Raw): string {
  const vs = (raw.videoSources ?? {}) as Record<string, unknown>
  for (const k of ['720', '480', '240']) {
    const url = vs[k]
    if (typeof url === 'string' && url) return url
  }
  return ''
}

function listStateIds(raw: Raw, flag: 'hasMedia' | 'canAddMedia'): string[] {
  const states = Array.isArray(raw.listStates) ? (raw.listStates as Raw[]) : []
  return states.filter((st) => st[flag] === true).map((st) => String(st.id))
}

export function toItem(raw: Raw): VaultItem {
  return {
    inCategories: listStateIds(raw, 'hasMedia'),
    addableTo: listStateIds(raw, 'canAddMedia'),
    id: num(raw.id),
    type: asType(raw.type),
    createdAt: String(raw.createdAt ?? ''),
    duration: num(raw.duration),
    thumb: thumbOf(raw),
    small: smallOf(raw),
    full: (asType(raw.type) === 'video' && streamOf(raw)) || fullOf(raw),
  }
}

/** OnlyFans takes one photo or video as a story ("Add to your story" is greyed for more). */
export function canStory(selection: VaultItem[]): boolean {
  return selection.length === 1 && (selection[0]!.type === 'photo' || selection[0]!.type === 'video')
}
